import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import type { ClientPackageLimits, ClientPackageModules, ClientPackagePreset } from './types';

/**
 * Module-aware UI (docs/ARCHITECTURE_V2.md §3.4/§6, §12 agent 7): backed by
 * `GET /dashboard/client-package`, which the Client Worker (apps/client-api/src/routes/dashboard/
 * extras.ts) now serves, returning exactly `{presetName, modules, limits}` per §6's contract -
 * confirmed by reading that route directly (`getClientPackage(db, tenantId)`, gated by
 * `requireHumanDashboardSession`, tenant taken only from the verified session, never spoofable).
 *
 * Same "courtesy, not the real gate" rule as `lib/auth.tsx`'s `RequirePermission`: every
 * module-gated server route re-checks its own flag independently (§6: "no route trusts the other
 * Worker already checked"), so a client that somehow bypasses this UI still gets a real 403/404
 * from the server, never a silent bypass. This only decides whether to show, gray out, or label a
 * screen the owner's package doesn't include - per the owner's exact wording (§3.4): "Not included
 * in your package, contact Loctopus."
 */
interface ClientPackageState {
  presetName: ClientPackagePreset | null;
  modules: ClientPackageModules | null;
  limits: ClientPackageLimits | null;
  isLoading: boolean;
  /** True only once we've positively confirmed the module is OFF (loaded + false). While loading,
   * this defaults to `true` (fail OPEN on the UI's courtesy check) - the one real gate is still
   * the server, so a brief loading flash should never read as "not included" before we actually
   * know; see each page's own `clientPkg.isLoading` check for where that matters. */
  hasModule: (name: keyof ClientPackageModules) => boolean;
}

const ClientPackageContext = createContext<ClientPackageState>({
  presetName: null,
  modules: null,
  limits: null,
  isLoading: true,
  hasModule: () => true,
});

export function ClientPackageProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const pkgQuery = useQuery({
    queryKey: ['client-package'],
    queryFn: api.clientPackage.get,
    // Only once a session exists - the route is session-gated (requireHumanDashboardSession), so
    // firing this before login would just be a guaranteed, noisy 401 on every page load.
    enabled: isAuthenticated,
    retry: false,
    staleTime: 60_000,
  });

  const value = useMemo<ClientPackageState>(() => {
    const modules = pkgQuery.data?.modules ?? null;
    return {
      presetName: pkgQuery.data?.presetName ?? null,
      modules,
      limits: pkgQuery.data?.limits ?? null,
      isLoading: pkgQuery.isLoading,
      hasModule: (name) => {
        // Fail open while loading/errored (never flash a false "not included" before the real
        // answer arrives) - the server is still the actual gate regardless of what this returns.
        if (!modules) return true;
        return Boolean(modules[name]);
      },
    };
  }, [pkgQuery.data, pkgQuery.isLoading]);

  return <ClientPackageContext.Provider value={value}>{children}</ClientPackageContext.Provider>;
}

export function useClientPackage(): ClientPackageState {
  return useContext(ClientPackageContext);
}

/** The owner's exact wording (§3.4). Used both inline (nav items) and as a full-page notice. */
export const MODULE_NOT_INCLUDED_MESSAGE = 'Not included in your package, contact Loctopus.';
